import {Modal, Button, Form} from '@goproblems/ui';
import {useState, useEffect} from 'react';
import {useAuthenticated, useSelector, getNodeA1Path} from '@utils';
import {Expectation, Tolerances} from '@api';
import {isEmpty} from 'lodash';
import {useTranslation} from 'react-i18next';

type ReportNodeModalProps = {
    show: boolean;
    onHide: () => void;
    onSubmit: (data: {
        path: string;
        description: string;
        isTestCase: boolean;
        expectations: Expectation[];
        tolerances?: Tolerances;
    }) => void;
};

export const ReportNodeModal = ({
    show,
    onHide,
    onSubmit,
}: ReportNodeModalProps) => {
    const {t} = useTranslation();
    const {currentNode} = useSelector(s => s.board);
    const path = getNodeA1Path(currentNode);
    const {isAdmin} = useAuthenticated();
    const [description, setDescription] = useState('');
    const [shouldEnd, setShouldEnd] = useState(false);
    const [isTestCase, setIsTestCase] = useState(false);

    useEffect(() => {
        if (show) {
            setDescription('');

            const endnessProp = currentNode?.model?.customProps?.find(
                (p: {token: string}) => p.token === 'ED'
            );
            const endness = endnessProp ? parseFloat(endnessProp.value) : null;

            const defaultShouldEnd = endness !== null ? endness > 0 : false;
            setShouldEnd(defaultShouldEnd);
            setIsTestCase(false);
        }
    }, [show, currentNode]);

    const handleSubmit = () => {
        onSubmit({
            path,
            description,
            isTestCase,
            expectations: [
                {
                    path,
                    expected: {
                        endness: shouldEnd,
                    },
                },
            ],
        });
        onHide();
    };

    return (
        <Modal show={show} onHide={onHide} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>{t('report_node.title')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="mb-3">
                    <strong>{t('common.path')}:</strong>{' '}
                    <span
                        className="d-inline-block"
                        style={{wordBreak: 'break-all'}}>
                        {isEmpty(path) ? '<root>' : path}
                    </span>
                </div>

                <Form.Group className="mb-3">
                    <Form.Label>{t('report_node.description')}</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={4}
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        placeholder={t('report_node.description_placeholder')}
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <div className="flex gap-4">
                        <Form.Check
                            type="radio"
                            id="shouldEndYes"
                            name="shouldEnd"
                            label={t('report_node.should_end')}
                            checked={shouldEnd === true}
                            onChange={() => setShouldEnd(true)}
                        />
                        <Form.Check
                            type="radio"
                            id="shouldEndNo"
                            name="shouldEnd"
                            label={t('report_node.should_not_end')}
                            checked={shouldEnd === false}
                            onChange={() => setShouldEnd(false)}
                        />
                    </div>
                </Form.Group>

                {isAdmin && (
                    <>
                        <hr className="my-4" />
                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                id="isTestCase"
                                label={t('report_node.create_test_case')}
                                checked={isTestCase}
                                onChange={e => setIsTestCase(e.target.checked)}
                            />
                            <Form.Text className="text-muted">
                                {t('report_node.test_case_help')}
                            </Form.Text>
                        </Form.Group>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    {t('common.cancel')}
                </Button>
                <Button variant="primary" onClick={handleSubmit}>
                    {t('common.submit')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
